import LegalLayout from "@/components/LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      lastUpdated="February 18, 2026"
      testId="page-terms"
      noIndex
    >
      <p>
        Welcome to Facetra. These Terms of Service (<strong>"Terms"</strong>) govern your access
        to and use of the Facetra platform, including the website at{" "}
        <a href="https://facetra.studio">facetra.studio</a> and the design studio at{" "}
        <a href="https://facetra.studio/app">facetra.studio/app</a> (collectively, the{" "}
        <strong>"Service"</strong>), operated by <strong>Facetra Design Studio Private Limited</strong>{" "}
        (<strong>"Facetra"</strong>, <strong>"we"</strong>, <strong>"our"</strong>, or{" "}
        <strong>"us"</strong>). By creating an account, joining the waitlist, or using the
        Service, you agree to be bound by these Terms.
      </p>

      <h2>1. Eligibility & Account</h2>
      <p>
        You must be at least 18 years old and capable of forming a binding contract to use the
        Service. You agree to provide accurate information when signing in via Google and to
        keep your account secure. You are responsible for all activity that occurs under your
        account.
      </p>

      <h2>2. The Service</h2>
      <p>
        Facetra is an AI-powered jewellery design platform that transforms sketches and prompts
        into photorealistic renders, multi-view sheets, and CAD-ready reference materials,
        specialised for Indian jewellery styles including Jadau, Polki, Meenakari, Kundan,
        Temple, Antique Gold, and Rajwadi. The Service uses third-party AI models (currently
        Google Gemini via Emergent integrations) to generate images and material estimates.
      </p>

      <h3>2.1 Credits</h3>
      <p>
        Renders are metered in <strong>credits</strong>. New users receive trial credits on
        first sign-in. Additional credits can be purchased via the in-app top-up flow
        (processed by Razorpay) or earned through promotional coupon codes. Credits do not
        expire while the account remains active. Credits are non-transferable and have no cash
        value.
      </p>

      <h3>2.2 Plans</h3>
      <ul>
        <li><strong>Free</strong> — trial credits on signup, no recurring charge.</li>
        <li><strong>Edu</strong> — granted via institutional coupon, includes monthly credit refill, CAD export is restricted.</li>
        <li><strong>Paid</strong> — credit packs purchased via Razorpay, includes all features.</li>
      </ul>

      <h2>3. Your Content & Ownership</h2>
      <p>
        You retain all rights to your input sketches, prompts, reference images, and the
        renders generated for you (<strong>"User Content"</strong>). By using the Service, you
        grant Facetra a limited, non-exclusive licence to process User Content solely to
        provide the Service (including transmitting it to our AI provider and storing it for
        your retrieval). We do not sell User Content. We do not use your renders to train
        third-party models without explicit consent.
      </p>

      <h3>3.1 Your responsibilities</h3>
      <ul>
        <li>You must own or have permission to use any reference images you upload.</li>
        <li>You must not upload content that is unlawful, infringes intellectual property, depicts illegal acts, or violates third-party rights.</li>
        <li>You acknowledge that AI-generated renders are conceptual references and may require human craftsmanship judgement before manufacturing.</li>
      </ul>

      <h2>4. Acceptable Use</h2>
      <p>You agree NOT to:</p>
      <ul>
        <li>Reverse engineer, scrape, or attempt to extract the underlying models or system prompts.</li>
        <li>Use the Service to generate disallowed content (sexual content, violence, hate, deceptive deepfakes of real people, etc.).</li>
        <li>Resell raw API access or rebrand the Service as your own.</li>
        <li>Abuse rate limits, evade quotas, or attempt to bypass billing.</li>
        <li>Use the Service for any illegal purpose under Indian law or the laws applicable in your jurisdiction.</li>
      </ul>
      <p>
        We may suspend or terminate accounts that violate these terms, with or without prior
        notice depending on severity.
      </p>

      <h2>5. Payments</h2>
      <p>
        All payments are processed by <strong>Razorpay Software Pvt. Ltd.</strong>. Prices are
        listed in Indian Rupees (INR) inclusive of applicable taxes unless stated otherwise.
        By making a payment, you authorise the charge to your selected payment method. See
        our <a href="/refund">Refund Policy</a> for cancellation and refund terms.
      </p>

      <h2>6. Third-Party Services</h2>
      <p>
        The Service relies on third-party providers including Google (authentication and AI
        models), Razorpay (payments), Resend (transactional email), and MongoDB Atlas
        (storage). Your use of features that depend on these providers is also subject to
        their terms.
      </p>

      <h2>7. Disclaimers & Limitation of Liability</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind.
        Facetra does not guarantee that renders will be free of defects or suitable for
        manufacturing without further refinement. Material weight, gem carat, and price-range
        estimates produced by the Bill of Materials feature are AI-generated approximations
        based on visual inference and Feb-2026 Indian retail rate context — they are{" "}
        <strong>not</strong> a substitute for hallmarked weighing or trade-quote pricing.
      </p>
      <p>
        To the maximum extent permitted by law, Facetra's total liability arising out of or
        relating to the Service in any twelve-month period shall not exceed the amount you
        paid us during that period.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or terminate access for
        violation of these Terms. On termination, your right to access the Service ends
        immediately; we may retain backups and logs for the period required by applicable law.
      </p>

      <h2>9. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. We will post the revised version on this
        page and update the "Last updated" date. Continued use after a change constitutes
        acceptance.
      </p>

      <h2>10. Governing Law & Jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India. Any dispute shall be subject to the
        exclusive jurisdiction of the courts of <strong>Amravati, Maharashtra</strong>, India.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href="mailto:hello@facetra.studio">hello@facetra.studio</a>.
      </p>
    </LegalLayout>
  );
}
